import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Panel, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePermissions } from "@/permissions/PermissionContext";
import { EPermission } from "@/permissions/types";
import { useAddTransferTypeMutation } from "@/redux/apis/config/trip/transferTypes";
import { addToast } from "@/utils";
import { TransferTypeAdvancedSettings } from "./AdvancedSettings/TransferTypeAdvancedSettings";
import { TransferTypeCostField } from "./fields/TransferTypeCostField";
import { TransferTypeDescriptionField } from "./fields/TransferTypeDescriptionField";
import { TransferTypeNameField } from "./fields/TransferTypeNameField";
import { TransferTypeTripTypeField } from "./fields/TransferTypeTripTypeField";
import { addTransferTypeFormSchema, AddTransferTypeFormData, FIXED_TRIP_TYPES } from "./form";

export const AddTransferTypePanel = () => {
  return (
    <div className="flex flex-col items-start gap-3 lg:flex-row-reverse">
      <Content />
    </div>
  );
};

const Content = () => {
  const form = useForm<AddTransferTypeFormData>({
    defaultValues: {
      tripType: "journey_rate",
      costAdjustment: 0,
      isAdminOnly: false,
      isDispatchEnabled: true,
      isTaxExempt: false,
      isSpecialEvent: false,
      excludedCategories: [],
    },
    resolver: zodResolver(addTransferTypeFormSchema),
    shouldFocusError: true,
  });

  return (
    <FormProvider {...form}>
      <Panel className="flex w-full flex-col gap-5">
        <div>
          <Typography variant="h3">Add Transfer Type</Typography>
          <Typography className="text-neutral-dark-gray">Add new option for your type of transfers</Typography>
        </div>
        <Form />
      </Panel>
    </FormProvider>
  );
};

const Form = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const form = useFormContext<AddTransferTypeFormData>();
  const [addTransferType, { isLoading }] = useAddTransferTypeMutation();

  const handleCancel = () => {
    navigate("./..");
  };

  const handleSave = async (data: AddTransferTypeFormData) => {
    addTransferType({
      name: data.name,
      description: data.description,
      type: data.tripType,
      multiplier: data.costAdjustment,
      vehicle_category_exclusions: data.excludedCategories,
      is_admin_only: FIXED_TRIP_TYPES.includes(data.tripType) ? true : data.isAdminOnly,
      is_dispatch_enabled: data.isDispatchEnabled,
      is_special_event: data.isSpecialEvent,
      tax_status: data.isTaxExempt ? "exempt" : "liable",
      is_active: true,
    })
      .unwrap()
      .then(() => {
        addToast("success", "Transfer Type added successfully");
        navigate("./..");
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  return (
    <form onSubmit={form.handleSubmit(handleSave)} id="add-transfer-type-form" className="flex flex-col gap-5">
      <div className="flex w-full flex-col gap-5 md:flex-row">
        <TransferTypeNameField className="w-full" />
        <TransferTypeDescriptionField className="w-full" />
        <TransferTypeCostField className="w-[170px]" />
      </div>
      <TransferTypeTripTypeField />
      {hasPermission(EPermission.ProviderConfigTransferTypesAdvancedManage) && (
        <>
          <Divider className="mt-5" />
          <TransferTypeAdvancedSettings />
        </>
      )}
      <div className="col-span-2 flex justify-end gap-4">
        <Button onClick={handleCancel} variant="secondary" size="lg" disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" size="lg" type="submit" form="add-transfer-type-form" disabled={isLoading || !form.formState.isDirty}>
          {isLoading ? <Spinner /> : "Add Transfer Type"}
        </Button>
      </div>
    </form>
  );
};
